import type { ActivityOf } from '@wyn/content';
import { type BrewingSim, brewingSim, RACK_BAND } from '@wyn/game-core';
import { useRef } from 'react';
import { palette, withAlpha } from '../lib/theme.ts';
import { HoldButton, Stage } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { useRealtime } from './useRealtime.ts';

const W = 340, H = 190;

/** The Brewhouse: follow the mash schedule, then rack at the peak of fermentation. */
export function BrewingGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'brewing'>;
  const canvas = useRef<HTMLCanvasElement>(null);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);
  const curve = useRef<[number, number][]>([]);
  const bubbles = useRef<{ x: number; y: number; s: number; life: number }[]>([]);

  const rt = useRealtime<BrewingSim>({
    create: () => brewingSim(a, session.level, session.seed!),
    onFrame: (sim, dt) => {
      if (sim.phase === 'mash') {
        curve.current.push([sim.time, sim.temp]);
        drawMash(sim);
      } else if (sim.phase === 'ferment') {
        // Bubbles rise as fermentation quickens. Cosmetic only.
        if (Math.random() < sim.activity * dt * 30) bubbles.current.push({ x: 15 + Math.random() * 70, y: 0, s: 14 + Math.random() * 12, life: 1 });
        for (const b of bubbles.current) {
          b.y += 100 * dt;
          b.life -= dt;
        }
        bubbles.current = bubbles.current.filter((b) => b.life > 0);
      }
    },
    onDone: (_sim, trace) => onDone(trace),
  });

  function drawMash(sim: BrewingSim) {
    const c = canvas.current;
    if (!c) return;
    if (!ctx.current) {
      const d = window.devicePixelRatio || 1;
      c.width = W * d;
      c.height = H * d;
      ctx.current = c.getContext('2d')!;
      ctx.current.setTransform(d, 0, 0, d, 0, 0);
    }
    const g = ctx.current;
    const pal = palette();
    const T = sim.mashLength;
    const X = (t: number) => 12 + (t / T) * 316, Y = (c: number) => 180 - ((c - 35) / 50) * 170;
    g.clearRect(0, 0, W, H);
    g.fillStyle = pal.vellum;
    g.fillRect(0, 0, W, H);
    g.fillStyle = withAlpha(pal.gilt, 0.22);
    g.beginPath();
    for (let s = 0; s <= T; s += 0.1) g.lineTo(X(s), Y(sim.targetAt(s) + sim.band));
    for (let s = T; s >= 0; s -= 0.1) g.lineTo(X(s), Y(sim.targetAt(s) - sim.band));
    g.fill();
    g.strokeStyle = pal.gilt;
    g.setLineDash([4, 4]);
    g.beginPath();
    for (let s = 0; s <= T; s += 0.1) g.lineTo(X(s), Y(sim.targetAt(s)));
    g.stroke();
    g.setLineDash([]);
    g.strokeStyle = pal.ember;
    g.lineWidth = 2.5;
    g.beginPath();
    for (const [s, c] of curve.current) g.lineTo(X(s), Y(c));
    g.stroke();
    g.lineWidth = 1;
    g.strokeStyle = withAlpha(pal.ink, 0.35);
    g.beginPath();
    g.moveTo(X(sim.time), 0);
    g.lineTo(X(sim.time), H);
    g.stroke();
  }

  const sim = rt.sim;
  if (!sim) return null;
  const mashing = sim.phase !== 'ferment';
  return (
    <Stage fx={rt.fx}>
      <div className="text-xs text-ink-faint smallcaps">{mashing ? '1/2 · Mash' : '2/2 · Ferment'}</div>
      <p className="text-sm text-ink-dim">
        {mashing
          ? <>Hold <b>Stoke</b> (or Space) to raise the mash temperature and follow the golden band as it steps up. The fire reacts slowly, so start stoking before each rise.</>
          : sim.marked
            ? <>The yeast is working. <b>Rack</b> the ale into casks (or Space) while fermentation is at its fiercest, inside the gold band.</>
            : <>The yeast is working. Every batch of this brew peaks at its own height, and no band marks it. <b>Rack</b> the ale (or Space) as fermentation turns.</>}
      </p>
      {mashing ? (
        <>
          <canvas ref={canvas} className="mx-auto block aspect-[34/19] w-full max-w-[340px] border border-rule" aria-label="Mash schedule" />
          <div className="flex justify-between font-mono text-sm text-ink-dim">
            <span>Temperature</span>
            <span>{sim.temp.toFixed(1)}°C (target {sim.targetAt(sim.time).toFixed(0)}°C)</span>
          </div>
        </>
      ) : (
        <>
          <div className="relative mx-auto h-28 max-w-[220px] overflow-hidden rounded-t-lg rounded-b-[60px] bg-[radial-gradient(ellipse_at_50%_20%,#8a6236,#3a2618)]">
            {bubbles.current.map((b, i) => (
              <span key={i} className="absolute text-[#f3e2b0]" style={{ left: `${b.x}%`, bottom: b.y, fontSize: b.s, opacity: b.life }}>·</span>
            ))}
          </div>
          <div className="well relative h-7" role="meter" aria-label="Fermentation" aria-valuenow={Math.round(sim.activity * 100)}>
            <div className="absolute inset-y-0 left-0 bg-[#c9a24a]" style={{ width: `${Math.max(0, Math.min(1, sim.activity + (Math.random() - 0.5) * 0.08)) * 100}%` }} />
            {sim.marked && <div className="absolute inset-y-0 right-0 border-l-2 border-gilt bg-gilt/20" style={{ left: `${RACK_BAND * 100}%` }} />}
          </div>
          <div className="text-xs text-ink-faint">Fermentation</div>
        </>
      )}
      <HoldButton held={rt.isHeld()} {...rt.holdProps()} disabled={!!sim.result || sim.phase === 'pause'}>
        {mashing ? '🔥 Stoke' : '🛢️ Rack into casks'}
      </HoldButton>
    </Stage>
  );
}
